import { useLazyQuery } from '@apollo/client';
import type { PropsWithChildren, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import type { SearchSortInput } from '@sorare/core/src/__generated__/globalTypes';
import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LeftFilters,
  SearchLayoutContainer,
  SearchLayoutMain,
  VerticalScroll,
} from '@sorare/core/src/components/search/SearchLayout/ui';
import type {
  GallerySortName,
  GallerySortNames,
} from '@sorare/core/src/contexts/config';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { GQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { BACKEND_CARD_SEARCH_FILTERS } from '@sorare/core/src/contexts/graphql-search/backendCardSearchFilters.ts';
import { pickRenderedBackendFilters } from '@sorare/core/src/contexts/graphql-search/pickRenderedBackendFilters.ts';
import { useGQLSearch } from '@sorare/core/src/contexts/graphql-search/useGQLSearch.ts';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards/index.tsx';
import { useCompetitionEligibilityFilterValue } from '@sorare/core/src/contexts/searchCards/useCompetitionEligibilityFilterValue.ts';
import { useFavoriteFilterValue } from '@sorare/core/src/contexts/searchCards/useFavoriteFilterValue.ts';
import { useFixtureFilterValue } from '@sorare/core/src/contexts/searchCards/useFixtureFilterValue.ts';
import { useProbablePitchersFilterValue } from '@sorare/core/src/contexts/searchCards/useProbablePitchersFilterValue.ts';
import { useWatchlistFilterValue } from '@sorare/core/src/contexts/searchCards/useWatchlistFilterValue.ts';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import {
  joinFiltersWithAnd,
  sportFilter,
} from '@sorare/core/src/lib/algolia.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { MAPPED_SORTS } from '#src/hooks/search/useCardsSearchClient.ts';
import {
  MARKET_CARDS_SEARCH_FACET_VALUES_QUERY,
  MARKET_CARDS_SEARCH_QUERY,
} from '#src/hooks/search/useMarketCardsSearch.ts';
import { useDefaultFilters } from '#src/hooks/useDefaultFilters.ts';
import { BackendActiveFilters } from '#src/search/ActiveFilters/index.tsx';
import { FiltersManager } from '#src/search/FiltersManager/index.tsx';
import { BackendFiltersManagerDialog } from '#src/search/FiltersManagerDialog/index.tsx';
import { BackendSearchBox } from '#src/search/SearchBox/index.tsx';
import { BackendToggleFiltersManager } from '#src/search/ToggleFiltersManager/index.tsx';
import { messages as searchLayoutMessages } from '#src/searchCards/AdvancedCardSearch/SearchLayout.tsx';
import { BackendCardResultsFromGraphQL } from '#src/searchCards/BackendCardResultsFromGraphQL/index.tsx';
import { BackendSortCards } from '#src/searchCards/BackendSortCards/index.tsx';
import { BackendTotalResults } from '#src/searchCards/BackendTotalResults/index.tsx';
import { backendPrimaryMarketFilters as footballBackendPrimaryMarketFilters } from '#src/searchCards/football/cardFilters.tsx';
import { backendPrimaryMarketFilters as mlbBackendPrimaryMarketFilters } from '#src/searchCards/mlb/cardFilters.tsx';
import { backendPrimaryMarketFilters as nbaBackendPrimaryMarketFilters } from '#src/searchCards/nba/cardFilters.tsx';

const SortAndSegment = styled(Vertical).attrs({ gap: 1.5 })`
  @media ${laptopAndAbove} {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
  }
`;
const SortCtn = styled(Horizontal)`
  width: 100%;
  padding-bottom: var(--intermediate-unit);
  border-bottom: 1px solid rgba(var(--c-rgb-white), 0.11);
  @media ${laptopAndAbove} {
    width: auto;
    padding-bottom: 0;
    border-bottom: none;
  }
`;
const SearchCtn = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 0 var(--double-unit);
`;

const FiltersManagerWrapper = styled.div`
  margin-bottom: var(--double-unit);
`;

const auctionSorts: GallerySortNames = [
  'Cards On Sale Newly Listed',
  'Cards On Sale Ending Soon',
  'Cards On Sale Highest Price',
  'Cards On Sale Lowest Price',
  'Cards On Sale Highest Average Score',
];

const DEFAULT_AUCTION_SORT: GallerySortName = 'Cards On Sale Ending Soon';

const backendPrimaryMarketFiltersBySport = {
  [Sport.FOOTBALL]: footballBackendPrimaryMarketFilters,
  [Sport.NBA]: nbaBackendPrimaryMarketFilters,
  [Sport.BASEBALL]: mlbBackendPrimaryMarketFilters,
};

type Props = {
  sport: Sport;
  banner?: ReactNode;
};

const BackendAuctionsSearchContent = ({ sport, banner }: Props) => {
  const { up: isLaptop } = useScreenSize('laptop');
  const [filtersOpen, toggleFilters] = useToggle(false);
  const { formatMessage } = useIntl();
  const { promotionalEvents } = useConfigContext();

  const cardFilters =
    backendPrimaryMarketFiltersBySport[
      sport as keyof typeof backendPrimaryMarketFiltersBySport
    ]?.();

  const defaultFilters = useDefaultFilters({ auction: true });

  const activeAuctionFilters = useMemo(
    () =>
      pickRenderedBackendFilters(
        BACKEND_CARD_SEARCH_FILTERS,
        cardFilters ?? []
      ),
    [cardFilters]
  );

  const {
    urlToApi,
    urlToType,
    apiToUrl,
    activeFacets,
    refinements,
    facets,
    query,
    page,
    setSearchParams,
    searchParams,
  } = useGQLSearch(activeAuctionFilters);

  const currentSortName = (searchParams.get('s') ??
    DEFAULT_AUCTION_SORT) as GallerySortName;

  const { filters: searchCardsFilters } = useSearchCardsContext();

  const favoriteActive = !!activeFacets.ff?.length;
  const favoriteFilterValue = useFavoriteFilterValue({ skip: !favoriteActive });

  const competitionEligibility = activeFacets.lf?.[0];
  const competitionEligibilityFilterValue =
    useCompetitionEligibilityFilterValue(competitionEligibility);

  const gameweekSlug = activeFacets.gw?.[0];
  const gameweekFilterValue = useFixtureFilterValue({
    filterValue: gameweekSlug || undefined,
    eventType: So5FixtureEvent.CLASSIC,
  });

  const dailySlug = activeFacets.dly?.[0];
  const dailyFilterValue = useFixtureFilterValue({
    filterValue: dailySlug || undefined,
    eventType: So5FixtureEvent.DAILY,
  });

  const probablePitchersValue = activeFacets.pp?.[0];
  const probablePitchersFilterValue = useProbablePitchersFilterValue({
    probablePitchersFilterValue: probablePitchersValue,
    skip: !probablePitchersValue,
  });

  const promotionalEventSlug = activeFacets.promotion?.[0];
  const promotionalEventFilterValue = promotionalEvents.find(
    ({ slug }) => slug === promotionalEventSlug
  )?.algoliaFilters;

  const watchlistId = activeFacets.wl?.[0];
  const { watchlistFilterValue, loading: watchlistLoading } =
    useWatchlistFilterValue({ filterValue: watchlistId || undefined });

  const filtersLoading = watchlistLoading;

  const advancedFilters = joinFiltersWithAnd(
    [
      ...defaultFilters,
      ...searchCardsFilters,
      sportFilter(sport),
      favoriteActive && favoriteFilterValue,
      gameweekSlug && gameweekFilterValue,
      dailySlug && dailyFilterValue,
      probablePitchersValue && probablePitchersFilterValue,
      promotionalEventSlug && promotionalEventFilterValue,
      competitionEligibility && competitionEligibilityFilterValue,
      watchlistId && watchlistFilterValue,
    ].filter(Boolean) as string[]
  );

  const toSortInputs = (sortName: GallerySortName): SearchSortInput[] => {
    const s = MAPPED_SORTS[sortName];
    if (!s) return [];
    if (typeof s.field === 'string') {
      return [{ field: s.field, direction: s.direction }];
    }
    return [{ field: s.field[sport], direction: s.direction }];
  };

  const { data, loading, previousData } = useQuery(MARKET_CARDS_SEARCH_QUERY, {
    skip: filtersLoading,
    variables: {
      query,
      page,
      pageSize: 20,
      advancedFilters,
      sorts: toSortInputs(currentSortName),
      refinements,
      facets,
      onSaleOnly: true,
    },
  });

  const [search] = useLazyQuery(MARKET_CARDS_SEARCH_FACET_VALUES_QUERY, {
    variables: {
      query,
      advancedFilters,
      refinements,
      field: '',
      facetQuery: '',
      onSaleOnly: true,
    },
  });

  const safeData = data ?? previousData;
  const searchCards = safeData?.searchCards;
  const hitSlugs = searchCards?.hits?.map(h => h.slug) ?? [];

  const searchBoxPlaceholder = formatMessage(
    searchLayoutMessages.defaultPlaceholder
  );

  return (
    <GQLSearchContext
      value={{
        query,
        search,
        extractFacetValues: searchFacetData =>
          searchFacetData?.searchCardFacetValues,
        getRefinementType: k => urlToType[k],
        getRefinementApiAttribute: k => urlToApi[k],
        refinements,
        setSearchParams,
        page,
        pages: searchCards?.nbPages ?? 0,
        nbHits: searchCards?.nbHits ?? 0,
        advancedFilters,
        onSaleOnly: true,
        activeFacets,
        sorts: [currentSortName],
        facets: Object.fromEntries(
          (searchCards?.facets ?? []).map(({ field, ...rest }) => [
            apiToUrl[field] ?? field,
            rest,
          ])
        ),
      }}
    >
      <Vertical gap={3}>
        {banner}
        <SearchLayoutContainer>
          {!!isLaptop && (
            <VerticalScroll>
              <LeftFilters>
                <SearchCtn>
                  <BackendSearchBox placeholder={searchBoxPlaceholder} />
                  <Horizontal>
                    <BackendTotalResults />
                  </Horizontal>
                </SearchCtn>
                <FiltersManager filters={cardFilters} skipClearAllButton />
              </LeftFilters>
            </VerticalScroll>
          )}
          <SearchLayoutMain>
            <SortAndSegment>
              <SortCtn>
                {!isLaptop && (
                  <BackendSearchBox placeholder={searchBoxPlaceholder} />
                )}
                {!!isLaptop && <BackendSortCards indexes={auctionSorts} />}
              </SortCtn>
            </SortAndSegment>
            <BackendActiveFilters />
            <BackendCardResultsFromGraphQL
              hitSlugs={hitSlugs}
              searchLoading={loading || filtersLoading}
              nbHits={searchCards?.nbHits ?? 0}
              sport={sport}
              onlyPrimary
              hideSorareUser
              showDesktopFilter
            />
          </SearchLayoutMain>
        </SearchLayoutContainer>
        {!isLaptop && (
          <>
            <Portal id="above-bottom-bar-portal">
              <FiltersManagerWrapper>
                <BackendToggleFiltersManager toggleFilters={toggleFilters} />
              </FiltersManagerWrapper>
            </Portal>
            <BackendFiltersManagerDialog
              open={filtersOpen}
              onClose={toggleFilters}
              filtersGroups={cardFilters}
              sorts={auctionSorts}
            >
              <BackendTotalResults />
            </BackendFiltersManagerDialog>
          </>
        )}
      </Vertical>
    </GQLSearchContext>
  );
};

export const supportsBackendAuctionsSearch = (sport: Sport) =>
  sport in backendPrimaryMarketFiltersBySport;

export const BackendAuctionsSearch = ({ sport, banner }: Props) => {
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();

  const Wrapper = useCallback(
    ({ children: c }: PropsWithChildren) => (
      <CurrentMarketplaceUserProvider sport={sport}>
        {c}
      </CurrentMarketplaceUserProvider>
    ),
    [sport]
  );

  if (!supportsBackendAuctionsSearch(sport)) return null;

  return (
    <ConditionalWrapper wrap={!currentMarketplaceUserContext} Wrapper={Wrapper}>
      <SearchCardsContextProvider commonMarket={false}>
        <BackendAuctionsSearchContent sport={sport} banner={banner} />
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
